import { appConfig } from '@/config/app-config';
import {
  OnlineBookingSlotsByWeekDay,
  OnlineBookingSlotGeneratorFormModel,
  OnlineBookingSlotGroupedByDurationDayTypeAndWeekDay,
  OnlineBookingSlot,
  SlotDuration,
  DayType,
  WeekDay,
} from '@bookings/interfaces/schedule';
/* eslint-disable @typescript-eslint/no-explicit-any */
import BPCApiRouteHelper from '@core/helpers/bpc-api-route.helper';
import BPCApiServiceInstance from '@core/services/bpc-api-service.instance';
import { Schedule, ScheduleSeason } from '@bookings/interfaces/schedule';
import { SlotPriceCategory } from '@bookings/interfaces/slot-price-category';
import BPCCommonHelper from '@core/helpers/common-helper';

export default class ScheduleService {
  constructor(
    protected $routeHelper: BPCApiRouteHelper,
    protected $bpcApi: BPCApiServiceInstance,
  ) {}

  public async addNewSlots(
    bookingSlots: OnlineBookingSlotGroupedByDurationDayTypeAndWeekDay[],
    generatorFormModel: OnlineBookingSlotGeneratorFormModel,
  ): Promise<OnlineBookingSlotGroupedByDurationDayTypeAndWeekDay[]> {
    const duration = generatorFormModel.slot_duration;

    const index = bookingSlots.findIndex(x => x.slot_duration === duration);
    if (index < 0) {
      throw new Error('Duration model not found');
    }

    const durationModel = bookingSlots[index];
    if (!durationModel) {
      throw new Error('Duration model not found');
    }
    const newSlots = await this.generateSlots(generatorFormModel);
    let oldSlots = null;

    switch (generatorFormModel.day_type) {
      case 'business_day':
        oldSlots = durationModel.business_day_slots;
        durationModel.business_day_slots = this.mergeWeekDaysSlots(newSlots, oldSlots);

        break;
      case 'bank_holiday':
        oldSlots = durationModel.bank_holiday_slots;
        durationModel.bank_holiday_slots = this.mergeWeekDaysSlots(newSlots, oldSlots);

        break;
      case 'holiday':
        oldSlots = durationModel.school_holiday_slots;
        durationModel.school_holiday_slots = this.mergeWeekDaysSlots(newSlots, oldSlots);
        break;
    }
    // replace durationModel
    bookingSlots[index] = durationModel;
    return bookingSlots;
  }

  public async addNewSlotDuration(
    bookingSlots: OnlineBookingSlotGroupedByDurationDayTypeAndWeekDay[],
    duration: SlotDuration,
  ): Promise<OnlineBookingSlotGroupedByDurationDayTypeAndWeekDay[]> {
    const index = bookingSlots.findIndex(x => x.slot_duration === duration);
    if (index >= 0) {
      return bookingSlots;
    }

    const durationModel = {
      slot_duration: duration,
      business_day_slots: null,
      bank_holiday_slots: null,
      school_holiday_slots: null,
    };

    bookingSlots.push(durationModel);

    // sortByDuration
    bookingSlots.sort((a, b) => a.slot_duration - b.slot_duration);

    return bookingSlots;
  }

  public async removeSlotDuration(
    bookingSlots: OnlineBookingSlotGroupedByDurationDayTypeAndWeekDay[],
    duration: SlotDuration,
  ): Promise<OnlineBookingSlotGroupedByDurationDayTypeAndWeekDay[]> {
    const index = bookingSlots.findIndex(x => x.slot_duration === duration);
    if (index < 0) {
      return bookingSlots;
    }

    bookingSlots.splice(index, 1);
    return bookingSlots;
  }

  public async getSchedule(id: number): Promise<Schedule> {
    const path = this.$routeHelper.getApiPath(`schedules/${id}`);
    const response = await this.$bpcApi.get(path).toPromise();
    return response.data;
  }

  public async updateSchedule(scheduleId: number, data: Schedule): Promise<Schedule> {
    const path = this.$routeHelper.getApiPath(`schedules/${scheduleId}`);
    const formModel = this.prepareScheduleFormModel(data);
    const response = await this.$bpcApi.put(path, formModel).toPromise();
    return response.data;
  }
  public async createSchedule(data: Schedule): Promise<Schedule> {
    const path = this.$routeHelper.getApiPath(`schedules`);
    const formModel = this.prepareScheduleFormModel(data);
    const response = await this.$bpcApi.post(path, formModel).toPromise();
    return response.data;
  }

  private prepareScheduleFormModel(formModel: Schedule) {
    const calendarResources = formModel.calendar_resources.map(resource => {
      return resource?.id || resource;
    });
    const onlineBookingResources = formModel.online_booking_resources.map(resource => {
      return resource?.id || resource;
    });
    const onlineBookingResourcesV2 = formModel.online_booking_resources_v2.map(resource => {
      return resource?.id || resource;
    });
    return {
      ...formModel,
      calendar_resources: calendarResources,
      online_booking_resources: onlineBookingResources,
      online_booking_resources_v2: onlineBookingResourcesV2,
    };
  }

  public async getScheduleSeason(scheduleId: number, id: number): Promise<ScheduleSeason> {
    const path = this.$routeHelper.getApiPath(`schedules/${scheduleId}/schedule-seasons/${id}`);
    const response = await this.$bpcApi.get(path).toPromise();
    return response.data;
  }

  public async getScheduleSeasonNewModel(scheduleId: number): Promise<ScheduleSeason> {
    const path = this.$routeHelper.getApiPath(`schedules/${scheduleId}/schedule-seasons/new-model`);
    const response = await this.$bpcApi.get(path).toPromise();
    return response.data;
  }

  public async createScheduleSeason(
    scheduleId: number,
    season: ScheduleSeason,
  ): Promise<ScheduleSeason> {
    const path = this.$routeHelper.getApiPath(`schedules/${scheduleId}/schedule-seasons`);
    const response = await this.$bpcApi.post(path, season).toPromise();
    return response.data;
  }

  public async updateScheduleSeason(
    scheduleId: number,
    season: ScheduleSeason,
  ): Promise<ScheduleSeason> {
    const path = this.$routeHelper.getApiPath(
      `schedules/${scheduleId}/schedule-seasons/${season.id}`,
    );
    const response = await this.$bpcApi.put(path, season).toPromise();
    return response.data;
  }

  public async removeScheduleSeason(scheduleId: number, id: number): Promise<void> {
    const path = this.$routeHelper.getApiPath(`schedules/${scheduleId}/schedule-seasons/${id}`);
    await this.$bpcApi.delete(path).toPromise();
  }

  // NOTE Aux functions used in the service
  private async generateWeekDayOnlineBookingSlots(
    startTime: string,
    endTime: string,
    duration: SlotDuration,
    priceCat: SlotPriceCategory,
    dayType: DayType,
    weekDay: WeekDay,
  ): Promise<OnlineBookingSlot[]> {
    const commonHelper = new BPCCommonHelper();

    const startHour = startTime.split(':')[0];
    const startMinute = startTime.split(':')[1];

    const endHour = endTime.split(':')[0];
    const endMinute = endTime.split(':')[1];

    const start = new Date();
    start.setHours(Number(startHour));
    start.setMinutes(Number(startMinute));
    start.setSeconds(0);
    start.setMilliseconds(0);

    const end = new Date();
    end.setHours(Number(endHour));
    end.setMinutes(Number(endMinute));
    end.setSeconds(0);
    end.setMilliseconds(0);

    const slots = [];
    const current = new Date(start);
    // timestamp to avoid duplicated ids
    while (current < end) {
      const nextEndDateTime = new Date(current.getTime());
      nextEndDateTime.setMinutes(current.getMinutes() + duration);

      if (nextEndDateTime <= end) {
        const currentStartTime = commonHelper.formatDateTime2Time(current);
        slots.push({
          id: `${weekDay}-${currentStartTime}-${duration}-${dayType}-${priceCat.id}`,
          start_time: currentStartTime,
          slot_duration: duration,
          day_type: dayType,
          slot_price_category: priceCat,
          weekday: weekDay,
        });
      }
      current.setMinutes(current.getMinutes() + duration);
    }
    return slots;
  }

  private async generateSlots(
    formModel: OnlineBookingSlotGeneratorFormModel,
  ): Promise<OnlineBookingSlotsByWeekDay> {
    const slotsByWeekDay: OnlineBookingSlotsByWeekDay = {
      mon: [],
      tue: [],
      wed: [],
      thu: [],
      fri: [],
      sat: [],
      sun: [],
    };

    for (const weekDay of formModel.selected_week_days) {
      const slots = await this.generateWeekDayOnlineBookingSlots(
        formModel.start_time,
        formModel.end_time,
        formModel.slot_duration,
        formModel.slot_price_category,
        formModel.day_type,
        weekDay,
      );
      slotsByWeekDay[weekDay] = slots;
    }

    return slotsByWeekDay;
  }

  private mergeWeekDaysSlots(
    newSlots: OnlineBookingSlotsByWeekDay,
    oldSlots: OnlineBookingSlotsByWeekDay | null,
  ): OnlineBookingSlotsByWeekDay {
    // Aux function to merge single day slots
    const mergeSingleDaySlots = (
      newSlots: OnlineBookingSlot[] | null,
      oldSlots: OnlineBookingSlot[] | null,
    ): OnlineBookingSlot[] => {
      const oldSlotsNotInNew = oldSlots?.filter(slot => {
        return !newSlots?.some(newSlot => newSlot.start_time === slot.start_time);
      });
      const mergedSlots = [...(newSlots || []), ...(oldSlotsNotInNew || [])];
      // Sort by start time
      mergedSlots.sort((a, b) => {
        if (a.start_time < b.start_time) {
          return -1;
        }
        if (a.start_time > b.start_time) {
          return 1;
        }
        return 0;
      });
      return mergedSlots;
    };

    const mergedSlots: OnlineBookingSlotsByWeekDay = {
      mon: mergeSingleDaySlots(newSlots?.mon, oldSlots?.mon),
      tue: mergeSingleDaySlots(newSlots?.tue, oldSlots?.tue),
      wed: mergeSingleDaySlots(newSlots?.wed, oldSlots?.wed),
      thu: mergeSingleDaySlots(newSlots?.thu, oldSlots?.thu),
      fri: mergeSingleDaySlots(newSlots?.fri, oldSlots?.fri),
      sat: mergeSingleDaySlots(newSlots?.sat, oldSlots?.sat),
      sun: mergeSingleDaySlots(newSlots?.sun, oldSlots?.sun),
    };
    return mergedSlots;
  }
}

export const useScheduleService = (): ScheduleService => {
  return new ScheduleService(
    new BPCApiRouteHelper(),
    new BPCApiServiceInstance({ apiPath: appConfig.apiPathV2 }),
  );
};
